<template>
  <div class="bg-white p-6 dark:bg-gray-800 dark:border-gray-800 rounded-lg shadow-md border border-gray-100">
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-lg font-semibold">Sites by Division</h3>
      <GlassSelect
        v-model="currentView"
        @change="saveViewPreference"
        :options="[
          { value: 'bar', label: 'Bar Chart' },
          { value: 'doughnut', label: 'Doughnut Chart' },
          { value: 'numbers', label: 'Numbers View' },
        ]"
        :valueKey="'value'"
        :labelKey="'label'"
        placeholder="Select View"
        class="w-auto"
      />
    </div>

    <!-- Numbers View -->
    <div v-if="currentView === 'numbers'" class="space-y-2">
      <div
        v-for="(count, division) in divisionData"
        :key="division"
        class="flex items-center justify-between"
      >
        <span class="text-gray-600 dark:text-white">{{ division || "No Division" }}</span>
        <span class="text-gray-900 darl:text-white font-medium">{{ count }}</span>
      </div>
    </div>

    <!-- Bar Chart View -->
    <div v-else-if="currentView === 'bar'" class="h-80">
      <Bar :data="barChartData" :options="chartOptions" />
    </div>

    <!-- Doughnut Chart View -->
    <div v-else class="h-80">
      <Doughnut :data="doughnutChartData" :options="doughnutChartOptions" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import Cookies from "js-cookie";
import GlassSelect from "@/components/GlassSelect.vue";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { Bar, Doughnut } from "vue-chartjs";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  ChartDataLabels
);

const props = defineProps({
  divisionData: {
    type: Object,
    required: true,
  },
});

const currentView = ref("bar");
const COOKIE_NAME = "preferred-division-view";
const COOKIE_EXPIRES = 30;

onMounted(() => {
  const savedView = Cookies.get(COOKIE_NAME);
  if (savedView && ["bar", "doughnut", "numbers"].includes(savedView)) {
    currentView.value = savedView;
  }
});

const saveViewPreference = () => {
  Cookies.set(COOKIE_NAME, currentView.value, { expires: COOKIE_EXPIRES });
};

// Color palette
const colorPalette = [
  "#FF6B6B",
  "#4ECDC4",
  "#45B7D1",
  "#96CEB4",
  "#FFEEAD",
  "#FFD93D",
  "#6C5CE7",
  "#A8E6CF",
  "#FF8B94",
  "#91A6FF",
  "#88D8B0",
  "#FF9A76",
  "#B5D99C",
  "#E0BBE4",
  "#957DAD",
];

const generateColors = (count) => {
  return Array(count)
    .fill(0)
    .map((_, index) => colorPalette[index % colorPalette.length]);
};

const labels = computed(() => Object.keys(props.divisionData));
const data = computed(() => Object.values(props.divisionData));
const colors = computed(() => generateColors(labels.value.length));
const totalSites = computed(() =>
  data.value.reduce((sum, val) => sum + val, 0)
);

// Calculate max value for y-axis scaling
const maxValue = computed(() => Math.max(...data.value));
const suggestedMax = computed(() => Math.ceil(maxValue.value * 1.2));

// Bar Chart Data and Options
const barChartData = computed(() => ({
  labels: labels.value,
  datasets: [
    {
      data: data.value,
      backgroundColor: colors.value,
      borderColor: colors.value,
      borderWidth: 1,
      barPercentage: 0.6,
    },
  ],
}));

// Compute chart options based on the max value
const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      callbacks: {
        label: function (context) {
          return `${context.raw} sites`;
        },
      },
    },
    datalabels: {
      anchor: "end",
      align: "top",
      offset: 4,
      color: "rgb(17 24 39)",
      backgroundColor: "rgba(255, 255, 255, 0.8)",
      borderRadius: 4,
      padding: { left: 4, right: 4, top: 2, bottom: 2 },
      font: {
        weight: "bold",
        size: 12,
      },
      formatter: (value) => value || "",
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        precision: 0,
      },
      grid: {
        display: true,
      },
      suggestedMax: suggestedMax.value,
    },
    x: {
      grid: {
        display: false,
      },
    },
  },
}));

// Doughnut Chart Data and Options
const doughnutChartData = computed(() => ({
  labels: labels.value,
  datasets: [
    {
      data: data.value,
      backgroundColor: colors.value,
      borderColor: "white",
      borderWidth: 2,
    },
  ],
}));

const doughnutChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: "65%",
  plugins: {
    legend: {
      position: "right",
      labels: {
        usePointStyle: true,
        padding: 20,
        font: { size: 12 },
      },
    },
    tooltip: {
      callbacks: {
        label: function (context) {
          const value = context.raw;
          const percentage = ((value / totalSites.value) * 100).toFixed(1);
          return `${value} sites (${percentage}%)`;
        },
      },
    },
    datalabels: {
      color: "rgb(17 24 39)",
      backgroundColor: "rgba(255, 255, 255, 0.8)",
      borderRadius: 4,
      padding: { left: 4, right: 4, top: 2, bottom: 2 },
      font: {
        weight: "bold",
        size: 13,
      },
      formatter: (value, ctx) => {
        const percentage = ((value / totalSites.value) * 100).toFixed(0);
        return value > 0 ? `${percentage}%` : "";
      },
      textAlign: "center",
    },
  },
};
</script>
